import { useEffect, useRef } from "react";
import type {
  QuestionnaireAnswer,
  QuestionnaireQuestion,
} from "../../types/questionnaire";

interface QuestionStepProps {
  question: QuestionnaireQuestion;
  answer: QuestionnaireAnswer;
  error?: string;
  onChange: (answer: QuestionnaireAnswer) => void;
}

function SingleChoiceAnswer({
  question,
  answer,
  error,
  onChange,
}: QuestionStepProps) {
  const otherInputRef = useRef<HTMLInputElement>(null);
  const errorId = `${question.id}-error`;
  const helperId = question.helper ? `${question.id}-helper` : undefined;
  const describedBy = [helperId, error ? errorId : undefined]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (answer.value === "Otro") {
      otherInputRef.current?.focus();
    }
  }, [answer.value]);

  if (question.type !== "single") return null;

  return (
    <fieldset
      className="questionnaire-options"
      aria-describedby={describedBy || undefined}
      aria-invalid={Boolean(error)}
    >
      <legend className="questionnaire-sr-only">{question.title}</legend>
      {question.options.map((option, index) => {
        const optionId = `${question.id}-${index}`;

        return (
          <label className="questionnaire-option" key={option} htmlFor={optionId}>
            <input
              id={optionId}
              type="radio"
              name={question.id}
              value={option}
              checked={answer.value === option}
              onChange={() => onChange({ ...answer, value: option })}
            />
            <span className="questionnaire-option__content">
              <span className="questionnaire-option__indicator" aria-hidden="true" />
              <span>{option}</span>
            </span>
          </label>
        );
      })}

      {question.allowOther && answer.value === "Otro" ? (
        <div className="questionnaire-other">
          <label htmlFor={`${question.id}-other`}>Cuéntame un poco más</label>
          <input
            ref={otherInputRef}
            id={`${question.id}-other`}
            type="text"
            value={answer.other}
            maxLength={300}
            autoComplete="off"
            aria-invalid={Boolean(error && !answer.other.trim())}
            aria-describedby={error ? errorId : undefined}
            onChange={(event) =>
              onChange({ ...answer, other: event.currentTarget.value })
            }
          />
        </div>
      ) : null}

      {error ? (
        <p
          className="questionnaire-error"
          id={errorId}
          role="alert"
          tabIndex={-1}
        >
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

function LongTextAnswer({
  question,
  answer,
  error,
  onChange,
}: QuestionStepProps) {
  if (question.type !== "longText") return null;

  const errorId = `${question.id}-error`;
  const countId = `${question.id}-count`;

  return (
    <div className="questionnaire-long-answer">
      <label className="questionnaire-sr-only" htmlFor={question.id}>
        {question.title}
      </label>
      <textarea
        id={question.id}
        value={answer.value}
        placeholder={question.placeholder}
        maxLength={question.maxLength}
        rows={7}
        required
        aria-invalid={Boolean(error)}
        aria-describedby={`${countId}${error ? ` ${errorId}` : ""}`}
        onChange={(event) =>
          onChange({ ...answer, value: event.currentTarget.value })
        }
      />
      <div className="questionnaire-long-answer__meta">
        {error ? (
          <p
            className="questionnaire-error"
            id={errorId}
            role="alert"
            tabIndex={-1}
          >
            {error}
          </p>
        ) : (
          <span />
        )}
        <span id={countId} aria-label={`${answer.value.length} caracteres escritos`}>
          {answer.value.length}/{question.maxLength}
        </span>
      </div>
    </div>
  );
}

export function QuestionStep(props: QuestionStepProps) {
  const { question } = props;

  return (
    <>
      <div className="questionnaire-question__heading">
        <p className="questionnaire-eyebrow">Tu punto de partida</p>
        <h1 id="questionnaire-step-title" tabIndex={-1}>
          {question.title}
        </h1>
        {question.helper ? (
          <p className="questionnaire-helper" id={`${question.id}-helper`}>
            {question.helper}
          </p>
        ) : null}
      </div>

      {question.type === "single" ? (
        <SingleChoiceAnswer {...props} />
      ) : (
        <LongTextAnswer {...props} />
      )}
    </>
  );
}
